import { CheckCircle, Warning, Error as ErrorIcon } from "@mui/icons-material"
import { ProductLot } from "../../types"
import { ColumnDef } from "@tanstack/react-table"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import { Box, Chip } from "@mui/material"
import { AuditLotStatus } from "src/graphql/quark/quark.type"
const getStatusIcon = (status: ProductLot["status"]) => {
  switch (status) {
    case "MATCHED":
      return <CheckCircle color="success" />
    case "SHORT":
      return <Warning color="warning" />
    case "EXCESS":
      return <ErrorIcon color="error" />
    default:
      return null
  }
}
const getStatusColor = (status: AuditLotStatus) => {
  switch (status) {
    case AuditLotStatus.Matched:
      // Green for matched (success)
      return "#4caf50" // Material UI green[500]
    case AuditLotStatus.Short:
      // Orange for short (warning)
      return "#ff9800" // Material UI orange[500]
    case AuditLotStatus.Excess:
      // Red for excess (error)
      return "#f44336" // Material UI red[500]
    case AuditLotStatus.Discrepancy:
      // Purple for discrepancy (distinct, visible)
      return "#9c27b0" // Material UI purple[500]
    case AuditLotStatus.InAudit:
      // Blue for in audit (active process)
      return "#2196f3" // Material UI blue[500]
    case AuditLotStatus.Verified:
      // Teal for verified (confirmation)
      return "#009688" // Material UI teal[500]
    default:
      // Grey for unknown/default
      return "#bdbdbd" // Material UI grey[400]
  }
}
const getAuditSummaryColumns = (): ColumnDef<any>[] => [
  {
    accessorKey: "actionId",
    header: "Action ID",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          {getStatusIcon(row.original.status)}
          {row.original.actionId}
        </Box>
      )
    }
  },
  {
    accessorKey: "expectedQuantity",
    header: "Expected Quantity",
    enableColumnFilter: false,
    enableSorting: false,
    accessorFn: (row) => getDisplayPrice(row?.expectedQuantity ?? 0)
  },
  {
    accessorKey: "actualQuantity",
    header: "Actual Quantity",
    enableColumnFilter: false,
    enableSorting: false,
    accessorFn: (row) => getDisplayPrice(row?.actualQuantity ?? 0)
  },
  {
    accessorKey: "lotStockValue",
    header: "Lot Stock Value",
    enableColumnFilter: false,
    enableSorting: false,
    accessorFn: (row) => getDisplayPrice(row?.lotStockValue ?? 0)
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: {
      filterVariant: "select",
      selectOptions: [
        {
          label: "Counted",
          value: AuditLotStatus.Counted
        },
        {
          label: "Short",
          value: AuditLotStatus.Short
        },
        {
          label: "Excess",
          value: AuditLotStatus.Excess
        },
        {
          label: "Discrepancy",
          value: AuditLotStatus.Discrepancy
        },
        {
          label: "In Audit",
          value: AuditLotStatus.InAudit
        },
        {
          label: "Verified",
          value: AuditLotStatus.Verified
        }
      ]
    },
    cell: ({ row }) => {
      return (
        <Chip
          label={row.original?.status}
          size="small"
          sx={{
            borderColor: getStatusColor(row.original?.status),
            color: getStatusColor(row.original?.status)
          }}
          variant="outlined"
        />
      )
    }
  }
]

export { getAuditSummaryColumns }
